import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Badge,
  Banner,
  Button,
  Card,
  Container,
  HStack,
  Heading,
  Spinner,
  Text,
  VStack,
} from 'wss3-forge'
import {
  disconnectGoogleOAuth,
  getGoogleOAuthStatus,
  getMcpStatus,
  startGoogleOAuth,
  type GoogleOAuthStatus,
  type McpServer,
  type McpStatusResponse,
} from '../lib/api.js'

const STATUS_VARIANT: Record<McpServer['status'], 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  up: 'success',
  connecting: 'info',
  down: 'error',
  disabled: 'default',
}

const STATUS_LABEL: Record<McpServer['status'], string> = {
  up: 'connecté',
  connecting: 'connexion...',
  down: 'hors-ligne',
  disabled: 'désactivé',
}

function GoogleCalendarCard() {
  const qc = useQueryClient()
  const googleQuery = useQuery<GoogleOAuthStatus>({
    queryKey: ['oauth-google-status'],
    queryFn: getGoogleOAuthStatus,
    retry: false,
  })
  const googleConnect = useMutation({
    mutationFn: async () => {
      const url = await startGoogleOAuth()
      const popup = window.open(url, 'stark-google-oauth', 'width=520,height=640')
      if (!popup) {
        // Popup blocked, fall back to current-tab navigation.
        window.location.href = url
      }
    },
  })
  const googleDisconnect = useMutation({
    mutationFn: () => disconnectGoogleOAuth(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['oauth-google-status'] }),
  })

  useEffect(() => {
    const onMsg = (e: MessageEvent): void => {
      if (e.origin !== window.location.origin) return
      const d = e.data as { type?: string; ok?: boolean } | undefined
      if (d?.type === 'stark.oauth.google') {
        qc.invalidateQueries({ queryKey: ['oauth-google-status'] })
      }
    }
    window.addEventListener('message', onMsg)
    return () => window.removeEventListener('message', onMsg)
  }, [qc])

  return (
    <Card padding="md">
      <VStack gap="sm" fullWidth>
        <HStack justify="between" align="start" gap="sm" fullWidth>
          <VStack gap="xs" align="start">
            <Text weight="semibold">Google Calendar</Text>
            <Text size="sm" color="muted">
              Lecture et écriture sur tes calendriers Google. Token chiffré côté serveur.
            </Text>
          </VStack>
          {googleQuery.data?.connected ? (
            <Badge variant="success">connecté</Badge>
          ) : (
            <Badge variant="default">non connecté</Badge>
          )}
        </HStack>

        {googleQuery.isLoading && <Spinner size="sm" />}
        {googleQuery.error && (
          <Text size="sm" color="muted">
            Service indisponible côté serveur (config <code>GOOGLE_OAUTH_CLIENT_ID</code> + <code>SECRET</code>).
          </Text>
        )}

        {googleQuery.data && (
          <HStack justify="between" align="center" fullWidth>
            <Text size="sm" color="muted">
              {googleQuery.data.connected
                ? googleQuery.data.email ?? 'Compte connecté'
                : 'Stark recevra les permissions de lecture/écriture sur tes calendriers.'}
            </Text>
            {googleQuery.data.connected ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => googleDisconnect.mutate()}
                loading={googleDisconnect.isPending}
              >
                Déconnecter
              </Button>
            ) : (
              <Button
                variant="primary"
                size="sm"
                onClick={() => googleConnect.mutate()}
                loading={googleConnect.isPending}
              >
                Connecter
              </Button>
            )}
          </HStack>
        )}
      </VStack>
    </Card>
  )
}

export function ConnectorsPage() {
  const q = useQuery<McpStatusResponse>({
    queryKey: ['mcp-status'],
    queryFn: getMcpStatus,
    refetchInterval: 10_000,
  })

  const data = q.data
  const servers = data?.servers ?? []
  const empty = !q.isLoading && servers.length === 0

  return (
    <Container maxWidth="lg" py="2xl">
      <VStack gap="lg" fullWidth>
        <VStack gap="sm">
          <Heading level={2}>Connecteurs</Heading>
          <Text color="muted">
            Services et serveurs MCP que Stark peut invoquer pendant une conversation.
          </Text>
        </VStack>

        <VStack gap="sm" fullWidth>
          <Text size="sm" weight="medium">Services</Text>
          <GoogleCalendarCard />
        </VStack>

        <VStack gap="sm" fullWidth>
          <Text size="sm" weight="medium">Serveurs MCP</Text>

          {q.error && (
            <Banner variant="error" title="Échec du chargement">
              {String((q.error as Error).message)}
            </Banner>
          )}

          {q.isLoading && (
            <HStack gap="sm" align="center">
              <Spinner size="sm" />
              <Text size="sm" color="muted">Chargement…</Text>
            </HStack>
          )}

          {empty && (
            <Banner variant="info" title="Aucun serveur MCP configuré.">
              Ajoute des serveurs dans <code>mcp.config.json</code> à la racine du repo,
              puis redémarre stark-server.
            </Banner>
          )}

          <VStack gap="sm" fullWidth>
            {servers.map((s) => (
              <Card key={s.name} padding="md">
                <VStack gap="sm" fullWidth>
                  <HStack justify="between" align="start" gap="sm" fullWidth>
                    <VStack gap="xs" align="start">
                      <HStack gap="xs" align="center">
                        <Text weight="semibold">{s.name}</Text>
                        <Badge variant={STATUS_VARIANT[s.status]}>{STATUS_LABEL[s.status]}</Badge>
                      </HStack>
                      {s.description && (
                        <Text size="sm" color="muted">{s.description}</Text>
                      )}
                    </VStack>
                    <Text size="xs" color="muted" style={{ flexShrink: 0 }}>
                      {s.tool_count} outil(s)
                    </Text>
                  </HStack>

                  {s.error && (
                    <Text size="xs" color="error">
                      {s.error}
                    </Text>
                  )}

                  {s.tools.length > 0 && (
                    <HStack gap="xs" wrap>
                      {s.tools.map((t) => (
                        <Badge key={t} variant="default">{t}</Badge>
                      ))}
                    </HStack>
                  )}
                </VStack>
              </Card>
            ))}
          </VStack>
        </VStack>
      </VStack>
    </Container>
  )
}
